import { useEffect } from 'react'
import useGetVersionKey from './useGetVersionKey'
import { getSystemUpdateApi } from '@/api/common'
import { useDispatch, useSelector } from 'react-redux'
import useLocalStorageWithExpiry from '@/utils/useLocalStorageWithExpiry'
import { SUCCESS_CODE, ECHO_VERSION } from '@/utils/constants'
import { updateVersionData, updateVersionModalVisible, updateVersionDataLoading, type StoreState } from '@/store'

// 全局初始化标志，防止多次初始化
let isInitialized = false

// 获取更新日志和版本信息
const useGetVersionData = () => {
  const dispatch = useDispatch()
  const { versionData, versionModalVisible, versionDataLoading } = useSelector((state: StoreState) => state.global)

  const { VERSION_STORAGE_KEY } = useGetVersionKey()
  const { getItemWithExpiry, setItemWithExpiry } = useLocalStorageWithExpiry({
    key: VERSION_STORAGE_KEY,
    expiryInMinutes: 60 * 12
  })

  // 初始化版本数据（只执行一次）
  useEffect(() => {
    if (isInitialized || versionDataLoading) {
      return
    }

    const initVersionData = async () => {
      try {
        isInitialized = true
        dispatch(updateVersionDataLoading(true))

        // 从本地存储中获取版本信息
        const versionStorage = getItemWithExpiry()
        if (versionStorage) {
          // 如果本地存储中有版本信息，则将其设置为更新日志
          dispatch(updateVersionData(versionStorage))
          dispatch(updateVersionDataLoading(false))
        } else {
          // 如果本地存储中没有版本信息，则从后端获取
          const res = await getSystemUpdateApi()
          if (res.data.code === SUCCESS_CODE) {
            // 获取新版本号
            const newVersion = res.data.data.version
            // 更新 Redux store 中的版本号
            dispatch({
              type: 'update-version',
              payload: newVersion
            })
            // 将新版本信息存储到本地存储中
            setItemWithExpiry(res.data.data)
            // 更新版本数据
            dispatch(updateVersionData(res.data.data))

            // 获取旧版本号
            const oldVersion = localStorage.getItem(ECHO_VERSION)

            const shouldShowModal = oldVersion !== JSON.stringify(newVersion)
            if (shouldShowModal) {
              // 将新版本号存储到本地存储中
              localStorage.setItem(ECHO_VERSION, JSON.stringify(newVersion))
              // 显示弹窗
              dispatch(updateVersionModalVisible(true))
            }

            dispatch(updateVersionDataLoading(false))
          }
        }
      } catch (error) {
        console.error('获取版本数据失败:', error)
        dispatch(updateVersionDataLoading(false))
      }
    }

    initVersionData()
  }, [dispatch, getItemWithExpiry, setItemWithExpiry, versionDataLoading])

  // 设置弹窗可见性
  const setVisible = (visible: boolean) => {
    dispatch(updateVersionModalVisible(visible))
  }

  const versionOnClick = () => setVisible(true)

  return {
    updateLogs: versionData,
    visible: versionModalVisible,
    setVisible,
    versionOnClick
  }
}

export default useGetVersionData
